<script lang="ts" setup>

import {
  DocumentSubmissionType,
  DocumentSubmissionTypeLabels,
} from "~/types/enums/DocumentSubmissionType.enum";

import { Subscription } from "rxjs";

const props = defineProps({
  classroomId: {
    type: String,
    required: true,
  },
  assignmentId: {
    type: String,
    required: true,
  },
});

defineEmits(["close"]);

const classroomDataStore = useClassroomDataStore(
  useCurrentUID()!,
  props.classroomId
);

const { classroom, students } = storeToRefs(classroomDataStore);

const assignmentDataStore = useAssignmentDataStore(
  props.classroomId,
  props.assignmentId
);

const {
  assignment,
  ungradedDocuments,
  allowKWOSubmission,
  allowRoughDraftSubmission,
} = storeToRefs(assignmentDataStore);

const showKWOSubmission = computed(() => {
  if (assignment.value?.allowKWOSubmission != undefined) {
    return assignment.value.allowKWOSubmission;
  }

  return classroom.value?.allowKWOSubmission ?? false;
});

const showRoughDraftSubmission = computed(() => {
  if (assignment.value?.allowRoughDraftSubmission != undefined) {
    return assignment.value.allowRoughDraftSubmission;
  }

  return classroom.value?.allowRoughDraftSubmission ?? false;
});

const appUserStore = useAppUserDataStore();
const { hasIEWLMS } = storeToRefs(appUserStore);

const gradedDocuments = ref<SubmittedDocuments>([]);

const isLoading = ref(true);

const tabs = ref<Tabs>([]);
const subscription = ref<Subscription | undefined>();
onMounted(() => {
  subscription.value = assignmentDataStore
    .streamGradedDocuments()
    .subscribe((docs: SubmittedDocuments) => {
      isLoading.value = false;
      gradedDocuments.value = docs;
    });

  if (
    hasIEWLMS.value(classroom.value?.organizationId) == true &&
    showKWOSubmission.value
  ) {
    tabs.value.push({
      key: DocumentSubmissionType.keyWordOutline,
      label:
        DocumentSubmissionTypeLabels[DocumentSubmissionType.keyWordOutline]
          .shortLabel,
    });
  }

  if (
    hasIEWLMS.value(classroom.value?.organizationId) == true &&
    showRoughDraftSubmission.value
  ) {
    tabs.value.push({
      key: DocumentSubmissionType.roughDraft,
      label:
        DocumentSubmissionTypeLabels[DocumentSubmissionType.roughDraft]
          .shortLabel,
    });
  }

  tabs.value.push({
    key: DocumentSubmissionType.finalDraft,
    label:
      DocumentSubmissionTypeLabels[DocumentSubmissionType.finalDraft]
        .shortLabel,
  });
});

onUnmounted(() => {
  subscription.value?.unsubscribe();
});

const selectedTabKey = ref<DocumentSubmissionType>(
  DocumentSubmissionType.finalDraft
);

const ungradedDocumentsByType = computed(() => {
  return (documentSubmissionType: DocumentSubmissionType) => {
    const docs = ungradedDocuments.value.filter((document) => {
      // A lot of legacy documents don't have a submission type and we should consider those to be final drafts.
      if (documentSubmissionType == DocumentSubmissionType.finalDraft) {
        return (
          document.documentSubmissionType == undefined ||
          document.documentSubmissionType == documentSubmissionType
        );
      }

      return document.documentSubmissionType == documentSubmissionType;
    });

    return uniqueDocuments.value(docs);
  };
});

const gradedDocumentsByType = computed(() => {
  return (documentSubmissionType: DocumentSubmissionType) => {
    const docs = gradedDocuments.value.filter((document) => {
      // A lot of legacy documents don't have a submission type and we should consider those to be final drafts.
      if (documentSubmissionType == DocumentSubmissionType.finalDraft) {
        return (
          document.documentSubmissionType == undefined ||
          document.documentSubmissionType == documentSubmissionType
        );
      }

      return document.documentSubmissionType == documentSubmissionType;
    });

    return uniqueDocuments.value(docs);
  };
});

const uniqueDocuments = computed(() => {
  return (documents: SubmittedDocuments) => {
    const sortedDocuments = documents.sort(
      (a, b) =>
        (b.submittedAtTimestamp?.toMillis() ?? 0) -
        (a.submittedAtTimestamp?.toMillis() ?? 0)
    );

    const seenStudentIds = new Set<string>();

    const filteredDocuments = sortedDocuments.filter((document) => {
      if (document.studentId == undefined) return false;

      if (seenStudentIds.has(document.studentId)) {
        return false;
      }

      seenStudentIds.add(document.studentId);

      return true;
    });

    return filteredDocuments;
  };
});
</script>

<template>
  <BaseSheetComponent width="600px">
    <template #trigger>
      <slot />
    </template>
    <template #title> Assignment Summary </template>
    <template #content>
      <div>
        <BaseLoading v-if="isLoading" />
        <div class="flex flex-col">
          <BaseTabBar
            v-if="tabs.length > 1"
            v-model="selectedTabKey"
            class="m-4"
            :tabs="tabs"
          />
          <ClassroomAssignmentSummary
            v-if="isLoading != true"
            :key="selectedTabKey"
            class="grow"
            :students="students"
            :assignment="assignment!"
            :ungraded-documents="ungradedDocumentsByType(selectedTabKey)"
            :graded-documents="gradedDocumentsByType(selectedTabKey)"
          />
        </div>
      </div>
    </template>
  </BaseSheetComponent>
</template>

<style scoped>
.assignment-checklists-modal {
  z-index: 9999 !important;
}
.assignment-checklists-modal-content {
  display: flex;
  flex-direction: column;

  width: 600px;
  height: 90%;
  margin: auto;
  @apply rounded;
  margin-top: 64px;
}
</style>
